// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * A first-party HTML/XML text reader.
 *
 * WHY NOT DOMParser: it does not exist inside a dedicated worker, in any
 * engine. `typeof DOMParser` is "undefined" in DedicatedWorkerGlobalScope, and
 * a sibling tool lost a build day to discovering that after writing the parser
 * around it. Since DOCX and EPUB reading both belong in the worker — they are
 * CPU-bound and must not block the drop zone — the markup reading has to be
 * first-party.
 *
 * mammoth is the exception and works fine there: it bundles @xmldom/xmldom and
 * calls `new xmldom.DOMParser()` rather than reaching for the global.
 *
 * NAMESPACE PREFIXES ARE THE TRAP in EPUB specifically. An OPF package written
 * with an `opf:` prefix returns ZERO hits to /<item\s/ and four to /<\w*:?item\s/,
 * so every tag test here matches on the LOCAL NAME with an optional prefix.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  laquo: '«',
  raquo: '»',
  sbquo: '‚',
  bdquo: '„',
  shy: '­',
  ensp: ' ',
  emsp: ' ',
  thinsp: ' ',
  middot: '·',
  bull: '•',
  deg: '°',
  sect: '§',
  para: '¶',
  copy: '©',
  reg: '®',
  trade: '™',
  eacute: 'é',
  egrave: 'è',
  agrave: 'à',
  uuml: 'ü',
  ouml: 'ö',
  auml: 'ä',
  szlig: 'ß',
  ccedil: 'ç',
  ntilde: 'ñ',
  aelig: 'æ',
  oelig: 'œ',
};

export function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g, (whole, body: string) => {
    if (body[0] === '#') {
      const hex = body[1] === 'x' || body[1] === 'X';
      const code = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      try {
        return String.fromCodePoint(code);
      } catch {
        return whole;
      }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named ?? whole;
  });
}

/** Tags whose end produces a paragraph break rather than a space. */
const BLOCK_TAGS = new Set([
  'p', 'div', 'br', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'section', 'article', 'header', 'footer', 'table',
  'td', 'th', 'figcaption', 'dd', 'dt', 'pre', 'hr',
]);

/**
 * Markup to readable text.
 *
 * Block-level boundaries become newlines so that quotation extraction can still
 * see a blockquote as its own paragraph, and so a heading never runs into the
 * sentence beneath it and manufactures a phrase that exists in no source.
 */
export function markupToText(markup: string): string {
  // Anything inside these never renders and must not enter the text.
  let text = markup.replace(/<(script|style|head)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ');
  text = text.replace(/<!--[\s\S]*?-->/g, ' ');

  text = text.replace(/<\/?([a-zA-Z][\w:.-]*)\b[^>]*>/g, (_whole, tag: string) => {
    const local = tag.includes(':') ? tag.slice(tag.indexOf(':') + 1) : tag;
    return BLOCK_TAGS.has(local.toLowerCase()) ? '\n' : '';
  });
  text = text.replace(/<[^>]*>/g, '');
  text = decodeEntities(text);
  return text
    .replace(/[ \t ]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Read one attribute off the first element with the given local name. */
export function firstAttribute(xml: string, localName: string, attribute: string): string | null {
  const tag = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b([^>]*)>`, 'i');
  const m = tag.exec(xml);
  if (!m) return null;
  const attr = new RegExp(`\\b(?:[\\w.-]+:)?${attribute}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i').exec(m[1]);
  if (!attr) return null;
  return decodeEntities(attr[2] ?? attr[3] ?? '');
}

/** Every element with the given local name, as a map of its attributes. */
export function elementsWithAttributes(xml: string, localName: string): Record<string, string>[] {
  const tag = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b([^>]*?)/?>`, 'gi');
  const out: Record<string, string>[] = [];
  let m: RegExpExecArray | null;
  while ((m = tag.exec(xml)) !== null) {
    const attrs: Record<string, string> = {};
    const attrRe = /([\w.:-]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
    let a: RegExpExecArray | null;
    while ((a = attrRe.exec(m[1])) !== null) {
      const key = a[1].includes(':') ? a[1].slice(a[1].indexOf(':') + 1) : a[1];
      attrs[key.toLowerCase()] = decodeEntities(a[3] ?? a[4] ?? '');
    }
    out.push(attrs);
  }
  return out;
}
